﻿import { HubConnectionState } from "@microsoft/signalr";
import { useState, useEffect, useMemo, memo } from "react";
import { Skeleton, Paper, Autocomplete, TextField } from "@mui/material";
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell
} from "recharts";
import { useQuery } from '@tanstack/react-query';
import { faChartColumn } from "@fortawesome/free-solid-svg-icons";

import { dashboardsAjax } from "../../../ajax/DashboardsAjax";
import { useSignalRContext } from "../../Contexts/SignalRContext";
import { ErrorResponse } from "../../../DTOs/Common/ErrorResponse";
import { PlantTypeDTO } from "../../../DTOs/PlantTypeDTO";
import { plantAjax } from "../../../ajax/PlantAjax";
import useShiftTimes from "../../Hooks/useShiftTimes";
import { ShiftLockState } from "../../../utilities/helpers/shiftLockStateHelpers";
import { LiveIndicatorState } from "../DashboardCard";
import { DashboardCardEmptyState } from "../DashboardCardEmptyState";
import { useJustUpdated } from "../../Hooks/useJustUpdated";
import { buildHourlyMetricsChartData, hourlyMetricsReferenceMillis, pickDefaultPlantType } from "./hourlyMetricsChartData";

const LiveDot = (props: any) => {
  const { cx, cy, color, isEdge, showLive, pulse, value, labelDy } = props;
  if (cx == null || cy == null) return <g />;

  if (!showLive || !isEdge) {
    return <circle cx={cx} cy={cy} r={3} fill="var(--theme-surface-2)" stroke={color} strokeWidth={1} />;
  }

  return (
    <g>
      <circle
        cx={cx}
        cy={cy}
        r={5}
        fill="none"
        stroke="var(--theme-success)"
        strokeWidth={2}
        className="dashboard-live-ping-idle"
      />
      {pulse && (
        <circle
          cx={cx}
          cy={cy}
          r={5}
          fill="none"
          stroke="var(--theme-success)"
          strokeWidth={2}
          className="dashboard-live-ping"
        />
      )}
      <circle
        cx={cx}
        cy={cy}
        r={4.5}
        fill={color}
        stroke="var(--theme-success)"
        strokeWidth={1.5}
        strokeOpacity={0.4}
      />
      {value != null && (
        <text
          x={cx + 9}
          y={cy + (labelDy ?? -8)}
          fontSize={12}
          fontWeight={700}
          fill={color}
          className={pulse ? "dashboard-live-label-pulse" : ""}
        >
          {`${Math.round(value * 100)}%`}
        </text>
      )}
    </g>
  );
};

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;

  const recorded = payload.filter((p: any) => p.value != null);
  if (!recorded.length) return null;

  return (
    <div className="p-3 border rounded shadow bg-theme-surface">
      <p className="font-semibold text-theme-text text-lg">{label}</p>
      <div className="h-[1px] w-[3rem] bg-theme-border mt-1 mb-2"></div>
      {recorded.map((p: any) => (
        <p key={p.dataKey} style={{ color: p.color }}>
          {p.name}:{" "}
          {p.dataKey.includes("bcm")
            ? p.value.toFixed(0)
            : `${(p.value * 100).toFixed(1)}%`}
        </p>
      ))}
    </div>
  );
};

const ChartLegend = ({ payload, isFullScreen }: any) => (
  <ul className={`flex flex-wrap items-center justify-center gap-x-4 gap-y-1 m-0 p-0 mb-4 text-theme-table-heading-contrast ${isFullScreen
    ? "text-sm sm:text-base lg:text-lg xl:text-2xl short:text-sm"
    : "text-[10px] sm:text-xs md:text-sm short:text-[10px]"}`}>
    {payload?.map((entry: any) => (
      <li key={entry.value} className="flex items-center gap-1.5">
        <span
          className={entry.type === "line" ? "inline-block w-3 h-0.5 rounded-full" : "inline-block w-2.5 h-2.5 rounded-sm"}
          style={{ backgroundColor: entry.color }}
        />
        <span>{entry.value}</span>
      </li>
    ))}
  </ul>
);

type HourlyMetricsCardProps = {
  isFullScreen?: boolean;
  shiftLockState: ShiftLockState | null;
  onLiveIndicatorChange?: (state: LiveIndicatorState) => void;
};

export const HourlyMetricsCard: React.FC<HourlyMetricsCardProps> = memo(({ isFullScreen, shiftLockState, onLiveIndicatorChange }) => {
  const { selectedDate, selectedShift } = useShiftTimes();
  const { connection } = useSignalRContext();
  const [selectedPlantType, setSelectedPlantType] = useState<PlantTypeDTO | null>(null);

  const isCurrentShift = useMemo(() => {
    if (!shiftLockState || !selectedDate || !selectedShift) return false;

    return (
      shiftLockState.currentShift.dayOrNight === selectedShift.toString()[0] &&
      shiftLockState.currentShift.date.hasSame(selectedDate, "day")
    );
  }, [shiftLockState, selectedDate, selectedShift]);

  useEffect(() => {
    const connected = connection?.state === HubConnectionState.Connected;

    onLiveIndicatorChange?.({
      active: connected,
      showLabel: connected && isCurrentShift,
      tooltipText: isCurrentShift
        ? "Data displayed for the latest shift"
        : "Data displayed for selected shift",
    });
  }, [connection?.state, isCurrentShift, onLiveIndicatorChange]);

  const { data: plantTypes = [], isLoading: plantTypeLoading } = useQuery({
    queryKey: ['dashboard', 'plantTypes'],
    queryFn: async () => {
      const res = await plantAjax.getPlantTypes({ pageNumber: 1, pageSize: 100, jobTypes: 12 });
      if (ErrorResponse.isErrorResponse(res)) return [] as PlantTypeDTO[];
      return res.data;
    },
  });

  const sortedPlantTypes = useMemo(() => [...plantTypes].sort((a, b) => a.name.localeCompare(b.name)), [plantTypes]);

  const activePlantType = selectedPlantType ?? pickDefaultPlantType(sortedPlantTypes);

  const { data: hourlyMetrics = null, isPending: initialLoading, dataUpdatedAt } = useQuery({
    queryKey: ['dashboard', 'hourlyMetrics', selectedDate.toISODate(), selectedShift, activePlantType?.id],
    queryFn: async () => {
      const res = await dashboardsAjax.getHourlyMetricsCardData(selectedDate, selectedShift, activePlantType!.id!);
      if (ErrorResponse.isErrorResponse(res)) return null;
      return res;
    },
    enabled: !!activePlantType?.id && selectedDate.isValid && !!selectedShift,
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });

  const { points: chartData, liveEdgeIndex } = useMemo(
    () => buildHourlyMetricsChartData(hourlyMetrics, hourlyMetricsReferenceMillis(dataUpdatedAt), isCurrentShift),
    [hourlyMetrics, dataUpdatedAt, isCurrentShift]
  );

  const liveEdge = liveEdgeIndex >= 0 ? chartData[liveEdgeIndex] : null;
  const justUpdated = useJustUpdated(dataUpdatedAt, 2500);
  const showLive = isCurrentShift && liveEdge !== null;

  const noPlantTypes = !plantTypeLoading && sortedPlantTypes.length === 0;
  const isEmpty = noPlantTypes || (!initialLoading && chartData.length === 0);
  const showChartSkeleton = initialLoading && !isEmpty;

  return (
    <Paper className="!shadow-none !border-none !w-full h-full p-4 flex flex-col gap-2 md:gap-6 min-h-0 !bg-theme-surface-2">
      <Autocomplete
        size="small"
        options={sortedPlantTypes}
        loading={plantTypeLoading}
        value={activePlantType}
        onChange={(_, v) => setSelectedPlantType(v)}
        getOptionLabel={(o) => o.name}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        renderInput={(params) => (
          <TextField {...params} label="Plant Type" />
        )}
      />

      <div className="flex-1 min-h-0 relative">
        {showChartSkeleton && (
          <div className="animate-fade-in">
            <Skeleton
              variant="rectangular"
              className="absolute inset-0 rounded-lg !w-full !h-full !animate-pulse"
            />
          </div>
        )}

        {isEmpty && (
          <DashboardCardEmptyState
            isFullScreen={isFullScreen}
            icon={faChartColumn}
            title="No hourly metrics"
            subtitle={noPlantTypes
              ? "No plant types are available to display metrics for."
              : "There's no hourly data recorded for the selected plant, date and shift."}
          />
        )}

        {!isEmpty && chartData.length > 0 && (
          <ResponsiveContainer width="100%" height={isFullScreen ? '100%' : 522} debounce={400} className="animate-fade-in">
            <ComposedChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />

              <XAxis dataKey="displayHour" tick={{ className: "!fill-theme-table-heading-contrast" }} />
              <YAxis
                yAxisId="left"
                label={{ value: 'BCM', angle: -90, position: 'insideLeft', className: "!fill-theme-table-heading-contrast" }}
                tick={{ className: "!fill-theme-table-heading-contrast" }}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                label={{ value: '%', angle: -90, position: 'insideRight', className: "!fill-theme-table-heading-contrast" }}
                tick={{ className: "!fill-theme-table-heading-contrast" }}
                domain={[0, 1]}
                tickFormatter={(v) => `${(v * 100).toFixed(0)}%`}
              />

              <Tooltip content={<CustomTooltip />} />
              <Legend verticalAlign="top" content={<ChartLegend isFullScreen={isFullScreen} />} />

              <Bar
                yAxisId="left"
                dataKey="bcmTotal"
                name="BCM Total"
                fill="#2196F3"
              >
                {showLive && chartData.map((_, i) => (
                  <Cell
                    key={i}
                    fill="#2196F3"
                    stroke={i === liveEdgeIndex && justUpdated ? "var(--theme-success)" : undefined}
                    strokeWidth={i === liveEdgeIndex && justUpdated ? 2 : 0}
                  />
                ))}
              </Bar>
              <Bar
                yAxisId="left"
                dataKey="bcmPerHour"
                name="Productivity"
                fill="#90CAF9"
              />

              <Line
                yAxisId="right"
                dataKey="utilization"
                name="Utilization"
                stroke="#4CAF50"
                connectNulls={false}
                dot={(p: any) => <LiveDot {...p} color="#4CAF50" isEdge={p.index === liveEdgeIndex} showLive={showLive} pulse={justUpdated} labelDy={17} />}
              />
              <Line
                yAxisId="right"
                dataKey="availability"
                name="Availability"
                stroke="#df80ef"
                connectNulls={false}
                dot={(p: any) => <LiveDot {...p} color="#df80ef" isEdge={p.index === liveEdgeIndex} showLive={showLive} pulse={justUpdated} labelDy={-9} />}
              />
              <Line
                yAxisId="right"
                dataKey="availabilityForecastPerHour"
                name="Availability Forecast"
                stroke="#8b00a3"
              />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
    </Paper>
  );
});